import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "./landing/WaitlistModal";
import { openBooking } from "@/lib/booking";
import { IMAGES } from "./landing/data";

const BENEFITS = [
  { t: "₹0 to join", d: "No onboarding or listing fee — ever. Priests keep what they earn." },
  { t: "Steady bookings", d: "Income beyond a temple's daily collection plate, from families across the city." },
  { t: "You set your availability", d: "Accept only the ceremonies, dates and areas that suit you. We bring the families." },
  { t: "Support in Kannada", d: "Real people, real help — onboarding and support in your language." },
  { t: "Every tradition welcome", d: "Brahmin and non-Brahmin purohiths and poojaris across all communities." },
  { t: "Dignity first", d: "A verified profile that shows your training, traditions and experience to families." },
];

const STEPS = [
  { n: "1", t: "Register — free", d: "Share your details, traditions and the ceremonies you perform. KYC verification keeps the platform trusted." },
  { n: "2", t: "Set your availability", d: "Choose your dates, areas and the poojas you take on. You are always in control." },
  { n: "3", t: "We bring the families", d: "Get matched with families searching for exactly your knowledge — and earn with dignity." },
];

export default function PurohithsPage() {
  const [waitlist, setWaitlist] = useState({ open: false, role: "Purohith" });
  const openWaitlist = () => openBooking({ location: "purohiths" });

  useSeo({
    title: "For Purohiths & Poojaris — ₹0 to Join | Steady Bookings in Bengaluru | PurohithConnect",
    description: "Purohiths and poojaris — join PurohithConnect free of cost. Get steady bookings beyond temple collections, set your own availability, and earn with dignity. Support in Kannada. Every tradition welcome.",
    canonicalPath: "/purohiths",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "Is it free for purohiths to join PurohithConnect?", acceptedAnswer: { "@type": "Answer", text: "Yes. There is ₹0 onboarding or listing fee. Purohiths and poojaris keep what they earn from their bookings." } },
          { "@type": "Question", name: "Which traditions can register?", acceptedAnswer: { "@type": "Answer", text: "All of them — Smartha, Madhwa, Srivaishnava, Veerashaiva–Lingayat, kuladevata and community poojaris. Both Brahmin and non-Brahmin traditions are welcome." } },
          { "@type": "Question", name: "Can I choose my own availability?", acceptedAnswer: { "@type": "Answer", text: "Yes. You set your dates, service areas and the ceremonies you take on. PurohithConnect brings you matched families." } },
        ],
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "For Purohiths", item: "https://purohithconnect.com/purohiths" },
        ],
      },
    ]), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={openWaitlist} />
      <main>
        <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 pt-12 md:grid-cols-2 md:px-8">
          <div>
            <p className="font-mukta text-sm font-semibold uppercase tracking-widest text-[#A8722A]">For Purohiths & Poojaris</p>
            <h1 className="mt-2 font-eczar text-4xl font-bold leading-tight sm:text-5xl" data-testid="purohiths-title">
              Earn with <span className="text-[#B23A2B]">dignity</span>.
            </h1>
            <p className="mt-1 font-baloo text-lg text-[#A8722A]">ಪುರೋಹಿತರಿಗೆ ಗೌರವಯುತ ಆದಾಯ</p>
            <p className="mt-4 max-w-xl font-mukta text-base text-[#6B5044] md:text-lg">
              Many purohiths in rural Karnataka earn only what the day's visitors leave at the temple. PurohithConnect brings your knowledge and dedication to families in the city who are searching for exactly that trust — at ₹0 to join.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => openWaitlist("Purohith")} data-testid="purohiths-register-btn"
                className="rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]">Register as a purohith — free</button>
              <Link to="/occasions" data-testid="purohiths-see-occasions"
                className="rounded-full border border-[#A8722A] px-6 py-3 font-mukta font-semibold text-[#A8722A] hover:bg-[#A8722A]/10">See occasions families book</Link>
            </div>
          </div>
          <div>
            <img src={IMAGES.priestAfter} alt="A purohith performing a home ceremony for a family in Bengaluru"
              className="h-64 w-full rounded-[14px] object-cover shadow-md md:h-80" data-testid="purohiths-hero-image" />
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 md:px-8">
          <h2 className="text-center font-eczar text-3xl font-semibold md:text-4xl">Why purohiths choose us</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b) => (
              <div key={b.t} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5 shadow-sm" data-testid={`purohith-benefit-${b.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2">
                  <Check size={18} className="shrink-0 text-[#3E6B4F]" strokeWidth={2.5} />
                  <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{b.t}</h3>
                </div>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-[#A8722A]/20 bg-[#F3EADA]">
          <div className="mx-auto max-w-7xl px-4 py-16 md:px-8">
            <h2 className="text-center font-eczar text-3xl font-semibold md:text-4xl">How to join</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {STEPS.map((s) => (
                <div key={s.n} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm" data-testid={`purohith-step-${s.n}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#B23A2B] font-eczar text-lg font-bold text-[#FFFDF8]">{s.n}</span>
                  <h3 className="mt-4 font-eczar text-lg font-semibold text-[#3B221B]">{s.t}</h3>
                  <p className="mt-1.5 font-mukta text-sm text-[#6B5044]">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-4 py-16 text-center md:py-20">
          <blockquote className="font-eczar text-2xl font-medium leading-relaxed text-[#3B221B] md:text-3xl" data-testid="purohiths-mission-quote">
            "Every booking on this platform creates <span className="text-[#B23A2B]">rural employment</span> for a purohith whose income once depended on a temple's daily collection plate."
          </blockquote>
          <button onClick={() => openWaitlist("Purohith")} data-testid="purohiths-cta"
            className="mt-8 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]">Register as a purohith</button>
          <p className="mt-8 font-baloo text-lg text-[#A8722A]">|| ಶುಭಮಸ್ತು ||</p>
        </section>
      </main>
      <SiteFooter />
      <WaitlistModal key={`${waitlist.open}-${waitlist.role}`} open={waitlist.open} defaultRole={waitlist.role}
        onClose={() => setWaitlist((w) => ({ ...w, open: false }))} />
    </div>
  );
}
